const sentence = (...parts: string[]) => parts.join(" ");

export const aboutTranslations = {
  about: {
    hero: {
      tagline: "Our Story",
      title: "The home for software testers who want to grow, connect, and get hired",
      subtitle: sentence(
        "Quality Nexus is a global community built by testers, for testers. We help",
        "QA professionals stay ahead of market change, turn skills into income, and",
        "support companies in building world-class software quality teams.",
      ),
      primaryCta: "Join the community",
      secondaryCta: "Partner with us",
      imageAlt: "A diverse team of software testers collaborating around their laptops",
    },
    story: {
      label: "Why Quality Nexus exists",
      title: sentence(
        "We saw the market changing, and made it our responsibility to help testers",
        "grow through real learning",
      ),
      quote: "The best testers are not born — they are trained, connected, and supported.",
      paragraph1: sentence(
        "Quality Nexus started with a simple observation: the testing world is",
        "changing fast, and too many testers are left navigating it alone — buying",
        "expensive courses that promise everything and teach very little. During a",
        "casual outing, a group of QA friends turned that frustration into a",
        "conversation, and that conversation became a mission.",
      ),
      paragraph2: sentence(
        "We believe learning QA should not be confusing, costly, or driven by empty",
        "sales. We imagined a space where testers can learn with real guidance, find",
        "trusted mentors, share honest experiences, celebrate their wins, and turn",
        "their skills into real income. A place built by testers, for testers — and",
        "also for the companies that need them.",
      ),
      paragraph3: sentence(
        "That idea became Quality Nexus — a platform for people and businesses alike.",
        "We empower testers to grow and earn, help companies discover top QA talent,",
        "and deliver professional testing services for startups that are not ready to",
        "build large QA teams from day one.",
      ),
      imageAlt: "A software testing team reviewing quality reports together",
    },
    ecosystem: {
      label: "What you will find inside",
      title: "Everything a tester or QA team needs to move forward",
      subtitle: sentence(
        "A complete ecosystem for learning, networking, hiring, and testing —",
        "designed around the real needs of software testers and the companies that",
        "rely on them.",
      ),
      profiles: {
        title: "Standout profiles",
        description: sentence(
          "Showcase your skills, certifications, and projects so recruiters and",
          "companies can find the right QA talent faster.",
        ),
      },
      feed: {
        title: "Social feed & discussions",
        description: sentence(
          "Ask questions, share real experiences, and get honest advice from a global",
          "community of testers who understand the work.",
        ),
      },
      learning: {
        title: "Curated learning",
        description: sentence(
          "Access practical articles, guides, videos, and resources that keep you up to",
          "date with the tools and trends that matter.",
        ),
      },
      mentorship: {
        title: "Mentorship program",
        description: sentence(
          "Get matched with experienced QA mentors who help you navigate career",
          "changes, skill gaps, and next steps.",
        ),
      },
      study: {
        title: "Study groups & roadmaps",
        description: sentence(
          "Learn alongside peers through structured roadmaps, group sessions, and",
          "hands-on challenges built around real market needs.",
        ),
      },
      jobs: {
        title: "Jobs & opportunities",
        description: sentence(
          "Discover QA roles, freelance projects, and referrals that help you turn your",
          "testing expertise into real income.",
        ),
      },
      companies: {
        title: "Talent for companies",
        description: sentence(
          "Help companies find, evaluate, and hire the best software testers while",
          "improving the QA experience inside their teams.",
        ),
      },
      services: {
        title: "Testing services",
        description: sentence(
          "Get training, consulting, and professional testing services tailored to",
          "teams and projects of every size.",
        ),
      },
    },
    missionVision: {
      label: "Purpose",
      title: "Why we exist",
      subtitle: sentence(
        "We believe quality is not just a phase in development — it is a mindset, a",
        "craft, and a community that lifts everyone up.",
      ),
      mission: {
        title: "Our Mission",
        description: sentence(
          "To help software testers adapt to market change and grow their expertise",
          "through trusted learning, mentorship, and community. We also help companies",
          "find top QA talent and improve their team experience, while delivering",
          "professional testing services.",
        ),
      },
      vision: {
        title: "Our Vision",
        description: sentence(
          "To become the leading platform in MENA and globally where software testers",
          "learn, grow, and earn — and the trusted partner for companies building",
          "exceptional QA teams.",
        ),
      },
    },
    values: {
      label: "Values",
      title: "What drives us",
      subtitle: sentence(
        "These principles shape every decision, feature, and conversation inside",
        "Quality Nexus.",
      ),
      growth: {
        title: "Growth Mindset",
        description: sentence(
          "Every tester can keep evolving. We believe in learning, honest feedback, and",
          "embracing the tools and demands that shape tomorrow's QA work.",
        ),
      },
      community: {
        title: "Community First",
        description: sentence(
          "We rise by sharing knowledge, giving real feedback, celebrating wins, and",
          "supporting each other through the changes of our industry.",
        ),
      },
      innovation: {
        title: "Continuous Learning",
        description: sentence(
          "We stay curious about new methods, AI, and technologies so testers and teams",
          "can stay ahead in a fast-moving field.",
        ),
      },
      quality: {
        title: "Integrity & Trust",
        description: sentence(
          "We are honest, transparent, and committed to earning the trust of our",
          "members, partners, and the companies we serve.",
        ),
      },
    },
    timeline: {
      label: "Our journey",
      title: "How Quality Nexus came to life",
      subtitle: "A few milestones that mark the growth of our community and platform.",
      late2024: {
        year: "Late 2024",
        title: "The spark",
        description: sentence(
          "During a casual outing, a group of QA friends talked about how lost juniors",
          "and freshers feel in the testing world. By the end of the conversation, the",
          "idea of Quality Nexus was born.",
        ),
      },
      "2025_announce": {
        year: "2025",
        title: "We shared the vision",
        description: sentence(
          "We introduced Quality Nexus to the wider QA community, inviting testers and",
          "companies to join a platform built around learning, earning, and real",
          "growth.",
        ),
      },
      "2025_meetups": {
        year: "2025",
        title: "Meetups began",
        description: sentence(
          "We started organizing local meetups and study sessions to connect testers,",
          "share knowledge, and build a culture of genuine support.",
        ),
      },
      "2025_services": {
        year: "2025",
        title: "Testing services launched",
        description: sentence(
          "We began delivering professional testing services to companies, helping them",
          "ship better products without building large QA teams from day one.",
        ),
      },
      "2025_blog": {
        year: "2025",
        title: "Technical blog launched",
        description: sentence(
          "We launched our blog to share practical QA guides, real-world testing",
          "insights, and trusted learning material for the community.",
        ),
      },
      late2025_platform: {
        year: "Late 2025",
        title: "Building the platform",
        description: sentence(
          "Late in 2025 we started building the Quality Nexus platform to scale",
          "mentorship, learning, job opportunities, and crowd-sourced testing.",
        ),
      },
      soon_community: {
        year: "Soon",
        title: "Launching the community platform",
        description: sentence(
          "Releasing the full Quality Nexus community platform where testers can",
          "connect, learn, share knowledge, and grow together.",
        ),
      },
      soon_company: {
        year: "Soon",
        title: "Company registration",
        description: sentence(
          "Opening dedicated company profiles so businesses can contact quality",
          "professionals directly, showcase their teams, and post QA jobs to the right",
          "talent pool.",
        ),
      },
      soon_crowd: {
        year: "Soon",
        title: "Crowd testing platform",
        description: sentence(
          "Launching a crowd-sourced testing platform where companies can register",
          "projects and run real-world testing campaigns with trusted community",
          "testers.",
        ),
      },
    },
    cta: {
      title: "Ready to grow your QA career or team with us?",
      subtitle: sentence(
        "Join thousands of software testers and QA teams who are building better",
        "careers, better products, and a stronger community together.",
      ),
      primaryCta: "Join the community",
      secondaryCta: "Explore services",
    },
  },
};
